import { KeyboardEvent } from 'react';

export const programmaticallyCallOnChange = (event: KeyboardEvent<HTMLInputElement>, value?: string) => {
  // A way to programmatically call `onChange` handler
  // From: https://stackoverflow.com/a/46012210
  if (event.target instanceof HTMLInputElement) {
    const nativeInputValueSetter = Object?.getOwnPropertyDescriptor?.(window.HTMLInputElement.prototype, 'value')?.set;
    nativeInputValueSetter?.call(event.target, `${value ?? event.target.value}.`);
    const synthEvent = new Event('input', { bubbles: true });
    event.target.dispatchEvent(synthEvent);
    return event;
  }
  return event;
};

export const disallowNonNumericSymbols = (event: KeyboardEvent<HTMLInputElement>, maxDigitsAfterDot?: number) => {
  if (event.target instanceof HTMLInputElement) {
    const keyIsTab = event.key === 'Tab';
    // const keyIsNumber = Number.isFinite(+event.key);
    const keyIsNumber = /^[0-9]$/.test(event.key);
    const keyIsComma = event.key === ',';
    const keyIsDot = event.key === '.';
    const keyIsSpace = event.key === ' ';
    const keyIsBackspace = event.key === 'Backspace' || event.key === 'Delete';
    const keyIsSomeArrow = event.key.includes('Arrow');
    const keyIsZero = event.key === '0';
    const targetValue = event.target.value;
    const valueIsEmpty = !targetValue.length;
    const valueHasAlreadyDot = targetValue.includes('.');

    // Add a leading zero if user entered dot (. -> 0.)
    if (valueIsEmpty && keyIsDot) {
      event.preventDefault();
      return programmaticallyCallOnChange(event, '0');
    }

    // allow DevTools on/off
    if (event.key === 'Dead') return event;

    if (keyIsTab) return event;

    // prevent entering number after zero without dot (01)
    if (keyIsNumber && targetValue === '0') return event.preventDefault();

    // allow "select all" via ctrl+a combination
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') return event;

    // allow "copy" via ctrl+c combination
    if ((event.ctrlKey || event.metaKey) && event.key === 'c') return event;

    // allow "refresh" via ctrl+r combination
    if ((event.ctrlKey || event.metaKey) && event.key === 'r') return event;

    // allow "paste" via ctrl+v combination
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') return event;

    // prevent non-digit first symbol
    if (valueIsEmpty && !keyIsNumber) return event.preventDefault();

    // prevent more than 1 dot
    if (valueHasAlreadyDot && (keyIsDot || keyIsComma)) return event.preventDefault();

    // replace comma to dot
    if (keyIsComma) {
      event.preventDefault();
      return programmaticallyCallOnChange(event);
    }

    // prevent stacking leading zeros
    if (targetValue === '0' && keyIsZero) return event.preventDefault();

    // allow only digits, dot (at this moment comma was replaced yet), Arrows, Backspace
    if (!keyIsNumber && !keyIsDot && !keyIsBackspace && !keyIsSomeArrow) return event.preventDefault();
    if (keyIsSpace) return event.preventDefault();

    const [, valueAfterDot] = targetValue.split('.');
    if (maxDigitsAfterDot && valueHasAlreadyDot && keyIsNumber && valueAfterDot.length === maxDigitsAfterDot) {
      // return event.preventDefault();
    }
  }
};

export const checkAndFormatNumericInput = (value: string, maxDigitsAfterDot?: number) => {
  // value isn't numeric
  if (!Number.isFinite(+value)) return null;

  // maxDigitsAfterDot is not provided
  if (Number.isNaN(maxDigitsAfterDot)) return value.replace(/\./g, '');

  // case if the value isn't float
  if (!value.toString().includes('.')) return value;

  // value is float, we need to cut digits after a dot
  const [valueBeforeDot, valueAfterDot] = value.toString().split('.');

  if (maxDigitsAfterDot === 0) return valueBeforeDot;

  return `${valueBeforeDot}.${valueAfterDot.slice(0, maxDigitsAfterDot)}`;
};
